import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCustomerList,
  searchCustomer,
} from "../../../../redux/Thunk/AdminAccount.js/AccountThunk";
import Pagination from "../../../common/Pagination";
import Loader from "../../../Loader";
import Close from "../../../common/icons/Close";
import Search from "../../../common/icons/Search";
import AdminTable from "../../../common/Table";
import moment from "moment/moment";

const Customers = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(15);

  const { customer: data, loading } = useSelector((state) => state?.patient);

  useEffect(() => {
    if (
      !debouncedSearchTerm?.length &&
      (currentPage >= 1 || !data?.data?.length)
    ) {
      dispatch(getCustomerList({ page: currentPage, limit }));
    }
  }, [dispatch, currentPage, limit, debouncedSearchTerm, data?.data?.length]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);
  useEffect(() => {
    if (!debouncedSearchTerm?.length) {
      setFilteredData(data || []); // Full data when there's no search term
    }
  }, [data, debouncedSearchTerm]);

  useEffect(() => {
    const searchOrders = async () => {
      if (debouncedSearchTerm?.length >= 3) {
        // Define valid prefixes
        const validPrefixes = ["SID", "PID"];
        const searchPrefix = debouncedSearchTerm?.substring(0, 3).toUpperCase();

        // Dispatch action only if prefix is valid
        if (
          validPrefixes.includes(searchPrefix) ||
          debouncedSearchTerm?.length >= 3
        ) {
          try {
            const response = await dispatch(
              searchCustomer({
                search: debouncedSearchTerm?.trim(),
                page: currentPage,
                limit,
              })
            ).unwrap();

            if (response) {
              setFilteredData(response);
            } else {
              // Handle failure if needed
            }
          } catch (error) {
            console.error("Complete Error Object:", error);
          }
        }
      }
    };

    searchOrders(); // Call the async function
  }, [debouncedSearchTerm, currentPage, limit, dispatch]);

  const totalPages = data?.totalPages || 1;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "SN",
        accessor: (row, index) => {
          // Assuming you have currentPage and pageSize values
          const serialNumber = (currentPage - 1) * limit + index + 1;
          return serialNumber;
        },
        sticky: "left",
        width: 60,
      },
      {
        Header: "PID",
        accessor: (row) => row?.PID || row?.patientDetail?.PID,
        sticky: "left",
        width: 100,
      },
      {
        Header: "Full Name",
        accessor: (row) =>
          `${row?.name}  ${row?.surname}` ||
          `${row?.patientDetail?.name}  ${row?.patientDetail?.surname}`,
        sticky: "left",
        width: 200,
      },
       {
        Header: "DOB",
        accessor: (row) =>
          `${moment(row?.dateofbirth).format("DD/MM/YYYY")}`,
        width: 120,
      },
      {
        Header: "Contact",
        accessor: (row) => row?.phoneNumber || row?.patientDetail?.phoneNumber,
        width: 140,
      },
      {
        Header: "Gender",
        accessor: (row) =>
          `${row?.gender}`,
        width: 100,
      },
      {
        Header: "Store",
        accessor: (row) =>
          [...new Set(row?.orders?.map((order) => order?.SID))]
            .filter(Boolean) // This removes any undefined or null values
            .join(", ") || row?.SID,
        width: 100,
      },
      {
        Header: "Address",
        accessor: (row) => {
          return row?.patientAddress
            ? `${row?.patientAddress?.premisesNoFloor}, ${row?.patientAddress?.premisesName}, ${row?.patientAddress?.areaRoad}, ${row?.patientAddress?.landmark}, ${row?.patientAddress?.city}, ${row?.patientAddress?.state}, ${row?.patientAddress?.pincode}`
            : null;
        },
        width: 440,
      },
      {
        Header: "Contact2",
        accessor: (row) =>
          row?.patientAddress?.phoneNumber2 || row?.phoneNumber2,
        width: 120,
      },

    ],
    [currentPage, limit]
  );
  return (
    <div className="bg-gray-100 border border-primary-100 rounded-[8px] overflow-hidden">
      <Loader loading={loading} />
      <div className="relative flex items-center ">
        <span className="h-[24px] w-[24px] absolute left-3">
          <Search />
        </span>
        <input
          type="text"
          placeholder="Search by Name/PID/SID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.trimStart())}
          className="pl-[42px] w-full border-none bg-transparent shadow-none outline-none placeholder:text-primary-400 h-[40px] placeholder:text-[16px]"
        />
        {searchTerm && (
          <span
            className="h-[20px] w-[20px] absolute right-5 cursor-pointer"
            onClick={() => {
              setSearchTerm("");
              setDebouncedSearchTerm("");
            }}
          >
            <Close />
          </span>
        )}
      </div>
      <AdminTable columns={columns} filteredData={filteredData} />

      {/* Pagination Component */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        isPreviousDisabled={currentPage === 1}
        isNextDisabled={currentPage === totalPages}
        itemLength={data?.data?.length || data?.length || 0}
        totalItems={data?.totalItems || data?.totalItems}
      />
    </div>
  );
};

export default Customers;
